/**
 * EF-PLATFORM-AUTHENTICATION-V1: Login Page
 * EF-UI-STABILIZATION-AND-REGRESSION-FIX-V1: Added password visibility toggle and build number
 * EF-ONBOARDING-INVITATION-AND-BUILD-THEME-FIX-V1: Fixed build version
 * EF-PWA-BRANDING-V1: Added dynamic company branding
 * EF-PWA-BRANDING-V1-STEP2: Context-aware branding (platform vs tenant)
 * EF-BRANDING-SYSTEM-V2: Platform branding from settings
 * 
 * Platform login page with email/password authentication.
 * Redirects to dashboard on successful login.
 * 
 * Branding Logic:
 * - Platform context (eventsflo.ai/login) → Platform branding from settings
 * - Tenant context (subdomain/preview) → Tenant branding from API
 */

import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Loader2, Lock, Mail, AlertCircle, Sparkles, Eye, EyeOff } from "lucide-react";
// EF-ONBOARDING-INVITATION-AND-BUILD-THEME-FIX-V1: Import build version
import { BUILD_VERSION } from "../lib/version";
// EF-PWA-BRANDING-V1-STEP2: Import tenant context detection
// EF-BRANDING-SYSTEM-V2: Import fetchPlatformBranding
import { detectTenantContext, EVENTSFLO_BRANDING, fetchPlatformBranding } from "../lib/tenantContext";
// EVENTSFLO-LOGIN-PORTAL-CHOOSER-LINKS-V1
import PortalChooserLinks from "../components/PortalChooserLinks";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, completeTotp, startTotpEnrollment, isAuthenticated, loading: authLoading } = useAuth();
  
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  // EVENTSFLO-AUTH-PORTAL-MISMATCH-GUARDRAILS-V1
  const [portalMismatch, setPortalMismatch] = useState(null);
  // EF-UI-STABILIZATION-AND-REGRESSION-FIX-V1: Password visibility toggle
  const [showPassword, setShowPassword] = useState(false);

  // EVENTSFLO-SECURITY-P2-SUPERADMIN-TOTP-2FA-MINIMAL-V1
  // 2FA challenge state (SuperAdmin only).
  const [totpChallenge, setTotpChallenge] = useState(null);
  //   { challenge_token, action: "enroll"|"verify" }
  const [totpEnrollment, setTotpEnrollment] = useState(null);
  //   for action=enroll: { secret, otpauth_uri, qr_png_data_url }
  const [totpCode, setTotpCode] = useState("");
  const [totpUsingRecovery, setTotpUsingRecovery] = useState(false);
  const [totpRecoveryCode, setTotpRecoveryCode] = useState("");
  const [totpRecoveryCodes, setTotpRecoveryCodes] = useState(null);
  //   plaintext codes returned by enroll/verify, shown once
  const [totpError, setTotpError] = useState("");

  // EVENTSFLO-AUTH-PORTAL-MISMATCH-GUARDRAILS-V1
  // Prefill email from `?email=` query param so the "Go to ..." redirect
  // button on the OTHER portal lands the user with their email already
  // typed. Never prefill password.
  useEffect(() => {
    const params = new URLSearchParams(location.search || "");
    const prefill = (params.get("email") || "").trim().toLowerCase();
    if (prefill) setEmail(prefill);
  }, [location.search]);
  
  // EF-PWA-BRANDING-V1-STEP2: Context-aware branding state
  const [branding, setBranding] = useState(EVENTSFLO_BRANDING);
  const [tenantContext] = useState(() => detectTenantContext());

  // EF-PWA-BRANDING-V1-STEP2: Fetch branding based on context
  // EF-BRANDING-SYSTEM-V2: Use platform branding endpoint for platform context
  useEffect(() => {
    const fetchBranding = async () => {
      // Platform context: fetch platform branding from settings
      if (!tenantContext.isTenantContext) {
        console.log("Platform context detected, fetching platform branding from settings");
        const platformBranding = await fetchPlatformBranding();
        setBranding(platformBranding);
        return;
      }
      
      // Tenant context: fetch tenant branding from API
      console.log("Tenant context detected:", tenantContext.source, "- fetching tenant branding");
      
      try {
        const response = await fetch(`${API_URL}/api/public/branding`);
        if (response.ok) {
          const data = await response.json();
          setBranding({
            ...data,
            is_platform_branding: false
          });
        } else {
          // Fallback to platform branding on API error
          console.log("Tenant branding API failed, falling back to platform branding");
          const platformBranding = await fetchPlatformBranding();
          setBranding(platformBranding);
        }
      } catch (err) {
        console.log("Branding fetch failed, using platform branding fallback");
        const platformBranding = await fetchPlatformBranding();
        setBranding(platformBranding);
      }
    };
    fetchBranding();
  }, [tenantContext]);
  
  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !authLoading) {
      // EF-FIX-CREW-ONBOARDING-FLOW-END-TO-END-V1:
      // Don't auto-redirect here - let handleSubmit do the role-based redirect
      // This effect should only redirect if user manually navigates to /login while logged in
      const storedUser = localStorage.getItem("eventsflo_auth_user");
      let userData = null;
      try {
        userData = storedUser ? JSON.parse(storedUser) : null;
      } catch {}
      
      // Role-based redirect
      const userRole = userData?.role;
      let redirectPath = "/dashboard";
      
      if (userRole === "crew") {
        redirectPath = "/crew";
      } else if (location.state?.from?.pathname) {
        redirectPath = location.state.from.pathname;
      }
      
      navigate(redirectPath, { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate, location]);
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setPortalMismatch(null);

    if (!email || !password) {
      setError("Please enter both email and password");
      return;
    }

    setLoading(true);

    try {
      const result = await login(email, password);

      if (result.success) {
        // EF-FIX-CREW-ONBOARDING-FLOW-END-TO-END-V1:
        // Redirect based on role - crew members go to /crew portal
        const userRole = result.user?.role;
        let redirectPath = "/dashboard";

        if (userRole === "crew") {
          redirectPath = "/crew";
        } else if (location.state?.from?.pathname) {
          redirectPath = location.state.from.pathname;
        }

        navigate(redirectPath, { replace: true });
      } else if (result.totp_required) {
        // EVENTSFLO-SECURITY-P2-SUPERADMIN-TOTP-2FA-MINIMAL-V1
        // Password OK, now show the 2FA panel. For "enroll" action,
        // kick off /auth/totp/enroll/start so we can render the QR.
        setTotpChallenge({
          challenge_token: result.challenge_token,
          action: result.totp_action,
        });
        setError("");
        if (result.totp_action === "enroll") {
          const s = await startTotpEnrollment({ challenge_token: result.challenge_token });
          if (s?.success) {
            setTotpEnrollment(s);
          } else {
            setTotpError(s?.error || "Could not start 2FA enrollment");
          }
        }
      } else if (result.portal_mismatch) {
        // EVENTSFLO-AUTH-PORTAL-MISMATCH-GUARDRAILS-V1
        setPortalMismatch(result.portal_mismatch);
        setError("");
      } else {
        setError(result.error || "Login failed");
      }
    } catch (err) {
      setError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };
  
  // EVENTSFLO-SECURITY-P2-SUPERADMIN-TOTP-2FA-MINIMAL-V1
  const handleTotpSubmit = async (e) => {
    e.preventDefault();
    setTotpError("");
    if (!totpChallenge) return;
    const payload = totpUsingRecovery
      ? { recovery_code: totpRecoveryCode.trim() }
      : { code: totpCode.trim() };
    if (!payload.code && !payload.recovery_code) {
      setTotpError("Enter a code");
      return;
    }
    setLoading(true);
    try {
      const result = await completeTotp({
        challenge_token: totpChallenge.challenge_token,
        action: totpChallenge.action,
        ...payload,
      });
      if (result.success) {
        // If enroll returned recovery codes, show them ONCE before navigating.
        if (result.recovery_codes && result.recovery_codes.length) {
          setTotpRecoveryCodes(result.recovery_codes);
          // Keep the panel open on the "codes" view; user clicks Continue.
          return;
        }
        // Otherwise (verify path), go straight to dashboard.
        navigate("/dashboard", { replace: true });
      } else {
        setTotpError(result.error || "Invalid code");
        setTotpCode("");
        setTotpRecoveryCode("");
      }
    } finally {
      setLoading(false);
    }
  };

  // Show loading while checking auth
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-primary/3 rounded-full blur-3xl" />
      </div>
      
      <Card className="w-full max-w-md relative bg-card/80 backdrop-blur-sm border-white/10">
        <CardHeader className="text-center pb-2">
          {/* EF-PWA-BRANDING-V1-STEP2: Context-aware branding */}
          <div className="flex items-center justify-center mb-4">
            {branding.is_platform_branding ? (
              // Platform context: Always show EventsFlo logo
              <img 
                src="/eventsflo_logo.png" 
                alt="EventsFlo - Crew & Logistics Platform"
                className="h-16 md:h-20 max-h-20 object-contain"
                data-testid="eventsflo-logo"
              />
            ) : branding.logo_url ? (
              // Tenant context with custom logo
              <img 
                src={branding.logo_url} 
                alt={branding.company_name || "Company"} 
                className="h-16 md:h-20 max-h-20 object-contain"
                data-testid="tenant-logo"
                onError={(e) => {
                  // Fallback to EventsFlo logo if tenant logo fails
                  e.target.src = "/eventsflo_logo.png";
                  e.target.dataset.testid = "eventsflo-logo-fallback";
                }}
              />
            ) : (
              // Tenant context without custom logo: still show EventsFlo
              <img 
                src="/eventsflo_logo.png" 
                alt="EventsFlo - Crew & Logistics Platform"
                className="h-16 md:h-20 max-h-20 object-contain"
                data-testid="eventsflo-logo"
              />
            )}
          </div>
          <CardDescription>
            {/* EVENTSFLO-AUTH-PORTAL-MISMATCH-GUARDRAILS-V1 */}
            <span className="block font-semibold text-foreground">Admin Portal</span>
            <span className="block text-xs text-muted-foreground">
              {branding.is_platform_branding
                ? "For iCrew Services internal operations"
                : branding.has_custom_branding
                  ? `Sign in to ${branding.company_name}`
                  : "For iCrew Services internal operations"}
            </span>
            {/* EVENTSFLO-LOGIN-PORTAL-CHOOSER-LINKS-V1: Crew members sign in
                here too — same form, role-based redirect to /crew. */}
            <span
              className="block text-[11px] text-muted-foreground/80 mt-1"
              data-testid="admin-login-crew-hint"
            >
              Crew members also sign in here.
            </span>
          </CardDescription>
        </CardHeader>
        
        {/* EVENTSFLO-SECURITY-P2-SUPERADMIN-TOTP-2FA-MINIMAL-V1
            When a challenge is active, render the 2FA panel INSTEAD of
            the password form. The panel handles both enroll and verify
            actions.
        */}
        {totpChallenge ? (
          totpRecoveryCodes ? (
            // Recovery-codes reveal — shown once after successful enroll.
            <CardContent className="space-y-4" data-testid="totp-recovery-codes-panel">
              <Alert>
                <AlertDescription>
                  <div className="font-semibold mb-2">Save these recovery codes</div>
                  <div className="text-xs opacity-80">
                    Each code can be used once to sign in if you lose your
                    authenticator device. They will NOT be shown again.
                  </div>
                </AlertDescription>
              </Alert>
              <div className="grid grid-cols-2 gap-2 font-mono text-sm bg-black/40 p-3 rounded border border-white/10">
                {totpRecoveryCodes.map((c) => (
                  <div key={c} className="tracking-wider" data-testid="totp-recovery-code">
                    {c}
                  </div>
                ))}
              </div>
              <Button
                className="w-full"
                onClick={() => navigate("/dashboard", { replace: true })}
                data-testid="totp-recovery-continue-btn"
              >
                I've saved them — Continue
              </Button>
            </CardContent>
          ) : (
            <form onSubmit={handleTotpSubmit}>
              <CardContent className="space-y-4">
                <Alert>
                  <AlertDescription>
                    {totpChallenge.action === "enroll"
                      ? "Set up your Authenticator app"
                      : "Enter the 6-digit code from your Authenticator app"}
                  </AlertDescription>
                </Alert>
                {totpChallenge.action === "enroll" && totpEnrollment && (
                  <div className="space-y-3">
                    <div className="text-xs text-muted-foreground">
                      Scan the QR code with Google Authenticator, 1Password
                      or any TOTP app, then enter the 6-digit code below.
                    </div>
                    <div className="flex justify-center">
                      <img
                        src={totpEnrollment.qr_png_data_url}
                        alt="Authenticator QR code"
                        className="w-48 h-48 bg-white p-2 rounded"
                        data-testid="totp-qr-image"
                      />
                    </div>
                    <div className="text-[11px] font-mono break-all text-muted-foreground bg-black/30 p-2 rounded" data-testid="totp-secret-text">
                      Manual key: {totpEnrollment.secret}
                    </div>
                  </div>
                )}
                {!totpUsingRecovery ? (
                  <div className="space-y-2">
                    <Label htmlFor="totp-code">6-digit code</Label>
                    <Input
                      id="totp-code"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      maxLength={6}
                      value={totpCode}
                      onChange={(e) => setTotpCode(e.target.value.replace(/\D/g, ""))}
                      placeholder="000000"
                      data-testid="totp-code-input"
                    />
                  </div>
                ) : (
                  <div className="space-y-2">
                    <Label htmlFor="totp-recovery">Recovery code</Label>
                    <Input
                      id="totp-recovery"
                      autoComplete="off"
                      value={totpRecoveryCode}
                      onChange={(e) => setTotpRecoveryCode(e.target.value.toUpperCase())}
                      placeholder="ABCDEFGHJK"
                      data-testid="totp-recovery-input"
                    />
                  </div>
                )}
                {totpError && (
                  <Alert variant="destructive">
                    <AlertDescription>{totpError}</AlertDescription>
                  </Alert>
                )}
                <div className="flex flex-col gap-2">
                  <Button
                    type="submit"
                    className="w-full"
                    disabled={
                      loading ||
                      (!totpUsingRecovery && totpCode.length !== 6) ||
                      (totpUsingRecovery && totpRecoveryCode.trim().length < 8)
                    }
                    data-testid="totp-submit-btn"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : totpChallenge.action === "enroll" ? (
                      "Verify & Enable 2FA"
                    ) : (
                      "Verify"
                    )}
                  </Button>
                  {totpChallenge.action === "verify" && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setTotpUsingRecovery((v) => !v);
                        setTotpError("");
                      }}
                      data-testid="totp-toggle-recovery-btn"
                    >
                      {totpUsingRecovery ? "Use authenticator code" : "Use a recovery code"}
                    </Button>
                  )}
                </div>
              </CardContent>
            </form>
          )
        ) : (
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {/* EVENTSFLO-AUTH-PORTAL-MISMATCH-GUARDRAILS-V1 */}
            {portalMismatch && (
              <Alert
                className="bg-amber-500/10 border-amber-500/40 text-amber-200"
                data-testid="admin-login-portal-mismatch"
              >
                <AlertCircle className="h-4 w-4 text-amber-300" />
                <AlertDescription className="space-y-2">
                  <p className="text-sm">
                    This looks like a <strong>Client Portal</strong> account.
                    The Admin Portal won't recognise it. Please sign in via
                    the Client Portal instead.
                  </p>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="border-amber-500/40 text-amber-100 hover:bg-amber-500/20"
                    onClick={() => {
                      // Navigate WITHIN the SPA so React Router preserves
                      // state and the toast prefills via ?email=.
                      navigate(portalMismatch.redirect);
                    }}
                    data-testid="admin-login-portal-mismatch-cta"
                  >
                    Go to Client Portal →
                  </Button>
                </AlertDescription>
              </Alert>
            )}
            {/* Error Alert */}
            {error && (
              <Alert variant="destructive" className="bg-red-500/10 border-red-500/30 text-red-400">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            
            {/* Email Field */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium">
                Email Address
              </Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@eventsflo.ai"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  disabled={loading}
                  autoComplete="email"
                  data-testid="login-email"
                />
              </div>
            </div>
            
            {/* Password Field with visibility toggle */}
            <div className="space-y-2">
              <Label htmlFor="password" className="text-sm font-medium">
                Password
              </Label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 pr-10"
                  disabled={loading}
                  autoComplete="current-password"
                  data-testid="login-password"
                />
                {/* EF-UI-STABILIZATION-AND-REGRESSION-FIX-V1: Password visibility toggle */}
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  tabIndex={-1}
                  data-testid="toggle-password-visibility"
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" />
                  ) : (
                    <Eye className="h-4 w-4" />
                  )}
                </button>
              </div>
            </div>
          </CardContent>
          
          <CardFooter className="flex flex-col gap-4">
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Signing in...
                </>
              ) : (
                "Sign In"
              )}
            </Button>
            
            {/* EF-SECURE-ONBOARDING-AND-PASSWORD-RESET-LINKS-V1: Forgot Password link */}
            <a 
              href="/reset-password" 
              className="text-sm text-primary hover:underline text-center"
              data-testid="forgot-password-link"
            >
              Forgot your password?
            </a>

            {/* EVENTSFLO-LOGIN-PORTAL-CHOOSER-LINKS-V1: Portal chooser
                (Client + Crew links + Request quote). Replaces the old
                standalone "Request quote" CTA. */}
            <PortalChooserLinks currentPortal="admin" email={email} />
            
            <p className="text-xs text-muted-foreground text-center">
              Protected platform access. Contact administrator for account requests.
            </p>
            
            {/* EF-UI-STABILIZATION-AND-REGRESSION-FIX-V1: Build version display */}
            <p className="text-[10px] text-muted-foreground/50 text-center font-mono" data-testid="build-version">
              {BUILD_VERSION}
            </p>
          </CardFooter>
        </form>
        )}
      </Card>
    </div>
  );
}
